import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a crypto/fiat amount into a clean, human-readable string —
 * no more "+0.007161271238122131" or "130.21999999999".
 *
 * Rules:
 *  - >= 1: up to 4 decimals (2 for typical fiat display via `fiat=true`)
 *  - < 1 and > 0: up to 8 decimals (enough precision for small crypto amounts)
 *  - Trailing zeros are always trimmed
 *  - Falls back to '0' for NaN/undefined/null
 */
export function formatAmount(value: number | string | null | undefined, opts?: { fiat?: boolean }): string {
  const n = typeof value === 'string' ? parseFloat(value) : (value ?? 0)
  if (n === null || n === undefined || isNaN(n)) return '0'
  const abs = Math.abs(n)
  const decimals = opts?.fiat ? 2 : (abs >= 1 ? 4 : 8)
  const fixed = n.toFixed(decimals)
  // Trim trailing zeros (and a trailing decimal point) without going back to scientific notation
  return fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed
}

/** Same as formatAmount but always prefixes a +/- sign — used for deltas (P&L, deposits credited, etc). */
export function formatSignedAmount(value: number | string | null | undefined, opts?: { fiat?: boolean }): string {
  const n = typeof value === 'string' ? parseFloat(value) : (value ?? 0)
  const sign = n >= 0 ? '+' : ''
  return `${sign}${formatAmount(n, opts)}`
}
